import type { TaskLikeObject } from '@frontend/api-client';
import { match } from '@frontend/duck-tape';
import { useFeatureIsOn, useFeatureValue, useVisibilityController } from '@frontend/react';
import { useTaskLikeCard } from '@frontend/react-domain';
import { Icon, TabPill, Text, XStack, YStack } from '@frontend/web-react';
import { applyClass, concatClasses } from '@frontend/web-utils';
import { useAccessControl, useUpdateTaskMutation } from '@frontend/web/hooks';
import { appendRouteHistoryStack } from '@frontend/web/router';
import { handleMutation } from '@frontend/web/utils';
import { NavLinkWrapper } from '../../Navigation';
import { CloseTaskModal } from './CloseTaskModal';
import { GradientBorderCard } from './GradientBorderCard';
import { TaskCategoryIcon } from './TaskCategoryIcon/TaskCategoryIcon';
import { useTaskCardAnimation } from './hooks/useTaskCardAnimation';

type TaskLikeCardProps = {
  animationIndex?: number;
  isAnimated?: boolean;
  isAnimationExpanded?: boolean;
  isPressDisabled?: boolean;
  nextTriggerAt?: DateTimeISOString;
  showInlineActions?: boolean;
  spacing?: 'lg' | 'md';
  taskLikeObject: TaskLikeObject;
};

export const TaskLikeCard = ({
  animationIndex = 0,
  isAnimated = false,
  isAnimationExpanded = true,
  isPressDisabled,
  nextTriggerAt,
  showInlineActions = false,
  spacing = 'md',
  taskLikeObject,
}: TaskLikeCardProps) => {
  const { enabled: isGameplanPricingEnabled } = useFeatureIsOn('is_gameplan_pricing_enabled');
  const { isClientSubscribed } = useAccessControl();
  const [updateTask, { isLoading: isUpdatingTask }] = useUpdateTaskMutation();
  const [isCloseTaskModalOpen, { close: closeCloseTaskModal, open: openCloseTaskModal }] = useVisibilityController();
  const { value: markAsDoneDeployDate } = useFeatureValue('mark_as_done_deploy_date');
  const { animationStyle } = useTaskCardAnimation({ index: animationIndex, isExpanded: isAnimationExpanded });

  const {
    canUpdateTaskReadStatus,
    isCloseable,
    isRecurrenceInstance,
    isTitleEmphasized,
    isUnread,
    onUpdateTaskReadStatus,
    subtitle,
    title,
  } = useTaskLikeCard({
    handleMutation,
    markAsDoneDeployDate,
    nextTriggerAt,
    taskLikeObject,
    updateTask,
  });

  const navigateTo = match(taskLikeObject)
    .with({ __typename: 'Task' }, () => '/app/task/$taskId')
    .with({ __typename: 'DraftTask' }, () => '/app/draft-task/$draftTaskId')
    .exhaustive();

  const params = match(taskLikeObject)
    .with({ __typename: 'Task' }, (task) => ({ taskId: task.id }))
    .with({ __typename: 'DraftTask' }, (draftTask) => ({ draftTaskId: draftTask.id }))
    .exhaustive();

  const cardContent = isGameplanPricingEnabled ? (
    <NavLinkWrapper
      accessibilityLabel={title}
      className={concatClasses(
        'py-md group relative flex gap-md px-md items-center w-full grow-1 shrink-1 rounded-lg bg-trueWhite',
        applyClass(isUnread, 'border-0'),
        applyClass(!isUnread, 'border-1 border-cream-200'),
        applyClass(showInlineActions && isClientSubscribed, 'hover-background-effect'),
      )}
      disableHoverEffect
      disabled={isPressDisabled}
      params={params}
      state={{ routeHistoryStack: appendRouteHistoryStack('/app') }}
      style={{ boxShadow: isUnread ? 'none' : '0 2px 4px 0 rgba(0, 0, 0, 0.06)' }}
      to={navigateTo}
    >
      <YStack>
        <TaskCategoryIcon task={taskLikeObject} />
      </YStack>
      <YStack borderColor="neutral200" flexGrow={1} flexShrink={1} justifyContent="center">
        <XStack alignItems="center" justifyContent="between">
          <XStack flexGrow={1} flexShrink={1} gap="xs" justifyContent="between">
            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <Text
              className={concatClasses('text-neutral-950', isUnread ? 'font-[700]' : 'font-[500]')}
              lineClamp={1}
              type="p-md"
            >
              {title}
            </Text>
          </XStack>
          {isRecurrenceInstance || isUnread ? (
            <XStack
              accessibilityLabel={`${title} ${isRecurrenceInstance ? 'recurrence instance' : 'unread'} icon`}
              alignItems="center"
              className="max-md:flex md:group-hover:hidden"
              gapX="sm"
            >
              {isRecurrenceInstance ? <Icon className="text-textSecondary" name="IconRefresh" size={18} /> : null}
            </XStack>
          ) : null}
          {showInlineActions && isClientSubscribed ? (
            <XStack className="gap-xs hidden md:group-hover:flex absolute top-md right-md">
              {canUpdateTaskReadStatus ? (
                <TabPill
                  className="bg-cool-100 border-transparent"
                  color="primary"
                  disabled={!canUpdateTaskReadStatus || isUpdatingTask}
                  hoverEffect="opacity"
                  iconColor="primary"
                  label={isUnread ? 'Mark read' : 'Mark unread'}
                  leftIconName={isUnread ? 'IconMailOpened' : 'IconMail'}
                  onClick={onUpdateTaskReadStatus}
                  useCustomBackground
                />
              ) : null}
              {isCloseable ? (
                <TabPill
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  className="[background:linear-gradient(97.16deg,#EEFB86_-9.23%,#C7E0FF_106.01%)] border-transparent"
                  color="primary"
                  disabled={!isCloseable}
                  hoverEffect="opacity"
                  iconColor="primary"
                  label="Close task"
                  leftIconName="IconCheck"
                  onClick={openCloseTaskModal}
                  useCustomBackground
                />
              ) : null}
            </XStack>
          ) : null}
        </XStack>
        <XStack>
          <Text color="tertiary" type="p-sm">
            {subtitle}
          </Text>
        </XStack>
      </YStack>
    </NavLinkWrapper>
  ) : (
    <NavLinkWrapper
      className={concatClasses(
        'group relative flex gap-md px-md items-center w-full grow-1 shrink-1 rounded-lg bg-trueWhite',
        applyClass(isUnread, 'border-0'),
        applyClass(!isUnread, 'border-1 border-neutral-200'),
        applyClass(showInlineActions && isClientSubscribed, 'hover-background-effect'),
      )}
      disableHoverEffect
      disabled={isPressDisabled}
      params={params}
      state={{ routeHistoryStack: appendRouteHistoryStack('/app') }}
      to={navigateTo}
    >
      <YStack>
        <TaskCategoryIcon task={taskLikeObject} />
      </YStack>
      <YStack
        borderColor="neutral200"
        flexGrow={1}
        flexShrink={1}
        gap="xs"
        justifyContent="center"
        py={spacing}
        style={{ minHeight: 80 }}
      >
        <XStack alignItems="center" justifyContent="between">
          <XStack flexGrow={1} flexShrink={1} gap="xs" justifyContent="between">
            <Text lineClamp={1} type={isTitleEmphasized ? 'h5' : 'p-md'}>
              {title}
            </Text>
          </XStack>
          {isRecurrenceInstance || isUnread ? (
            <XStack alignItems="center" className="max-md:flex md:group-hover:hidden" gapX="sm">
              {isRecurrenceInstance ? <Icon className="text-textSecondary" name="IconRefresh" size={18} /> : null}
            </XStack>
          ) : null}
          {showInlineActions && isClientSubscribed ? (
            <XStack className="gap-xs hidden md:group-hover:flex absolute top-xs right-xs">
              {canUpdateTaskReadStatus ? (
                <TabPill
                  color="primary"
                  disabled={!canUpdateTaskReadStatus || isUpdatingTask}
                  hoverEffect="background"
                  iconColor="primary"
                  label={isUnread ? 'Mark read' : 'Mark unread'}
                  leftIconName="IconNotification"
                  onClick={onUpdateTaskReadStatus}
                />
              ) : null}
              {isCloseable ? (
                <TabPill
                  color="primary"
                  disabled={!isCloseable}
                  hoverEffect="background"
                  iconColor="primary"
                  label="Close task"
                  leftIconName="IconInbox"
                  onClick={openCloseTaskModal}
                />
              ) : null}
            </XStack>
          ) : null}
        </XStack>
        <XStack>
          <Text color="tertiary" type="p-sm">
            {subtitle}
          </Text>
        </XStack>
      </YStack>
    </NavLinkWrapper>
  );

  const cardWithBorder = isUnread ? <GradientBorderCard>{cardContent}</GradientBorderCard> : cardContent;

  const card = (
    <>
      {cardWithBorder}
      {taskLikeObject.__typename === 'Task' ? (
        <CloseTaskModal isOpen={isCloseTaskModalOpen} onClose={closeCloseTaskModal} task={taskLikeObject} />
      ) : null}
    </>
  );

  if (isAnimated) {
    return <div style={{ ...animationStyle, width: '100%' }}>{card}</div>;
  }

  return card;
};
